import { useCallback, useEffect, useMemo, useState } from 'react'
import { Boxes, Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useBranch } from '@/contexts/BranchContext'
import {
  createGoodsReceipt,
  fetchAdjustmentReasons,
  fetchGoodsReceipts,
  fetchInventoryProducts,
  fetchLowStockProducts,
  fetchStockBalances,
  fetchStockTakes,
  postGoodsReceipt,
  postPurchaseReturn,
  postStockAdjustment,
  postStockDamage,
  postStockTransfer,
} from '@/lib/api/inventory'
import { createProduct, fetchProductsList } from '@/lib/api/products'
import { createSupplier, fetchSuppliers, updateSupplier } from '@/lib/api/suppliers'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { SALON_DESK_SINGLE_STORE } from '@/lib/salonDeskConfig'
import { cn } from '@/lib/utils'

const TABS = [
  { id: 'list', label: 'Products' },
  { id: 'purchase', label: 'Purchase' },
  { id: 'suppliers', label: 'Suppliers' },
  { id: 'returns', label: 'Purchase return' },
  { id: 'transfer', label: 'Transfer' },
  { id: 'damage', label: 'Damage' },
  { id: 'stocktakes', label: 'Stock takes' },
  { id: 'adjust', label: 'Adjust' },
  { id: 'low', label: 'Low stock' },
].filter((tab) => !(SALON_DESK_SINGLE_STORE && tab.id === 'transfer'))

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function ProductsPage() {
  const { activeBranch, branches } = useBranch()
  const [tab, setTab] = useState('list')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [products, setProducts] = useState([])
  const [balances, setBalances] = useState([])
  const [lowStock, setLowStock] = useState([])
  const [suppliers, setSuppliers] = useState([])
  const [reasons, setReasons] = useState([])
  const [receipts, setReceipts] = useState([])
  const [stockTakes, setStockTakes] = useState([])
  const [productForm, setProductForm] = useState({
    name: '',
    barcode: '',
    cost_price: '',
    sell_price: '',
    min_stock: '0',
  })
  const [purchaseForm, setPurchaseForm] = useState({
    supplier_id: '',
    product_id: '',
    quantity: '1',
    unit_cost: '',
  })
  const [adjustForm, setAdjustForm] = useState({
    product_id: '',
    quantity_delta: '',
    reason_id: '',
    notes: '',
  })
  const [returnForm, setReturnForm] = useState({
    product_id: '',
    quantity: '1',
    supplier_id: '',
    notes: '',
  })
  const [transferForm, setTransferForm] = useState({
    product_id: '',
    quantity: '1',
    to_branch_id: '',
    notes: '',
  })
  const [damageForm, setDamageForm] = useState({
    product_id: '',
    quantity: '1',
    notes: '',
  })
  const [newSupplierName, setNewSupplierName] = useState('')
  const [editSupplierId, setEditSupplierId] = useState('')
  const [editSupplierName, setEditSupplierName] = useState('')

  const balanceByProduct = useMemo(() => {
    const map = new Map()
    for (const row of balances) {
      map.set(Number(row.product_id), Number(row.quantity_on_hand ?? 0))
    }
    return map
  }, [balances])

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [list, bal, low, sup, reasonList, grns, takes] = await Promise.all([
        fetchProductsList().catch(() => fetchInventoryProducts()),
        fetchStockBalances(),
        fetchLowStockProducts(),
        fetchSuppliers().catch(() => []),
        fetchAdjustmentReasons().catch(() => []),
        fetchGoodsReceipts().catch(() => []),
        fetchStockTakes().catch(() => []),
      ])
      setProducts(list ?? [])
      setBalances(bal ?? [])
      setLowStock(low ?? [])
      setSuppliers(Array.isArray(sup) ? sup : (sup?.items ?? []))
      setReasons(reasonList ?? [])
      setReceipts(grns ?? [])
      setStockTakes(takes ?? [])
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load products.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh, activeBranch?.id])

  async function handleCreateProduct(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await createProduct({
        name: productForm.name,
        sku: productForm.barcode?.trim() || `SKU-${Date.now()}`,
        barcode: productForm.barcode || null,
        cost: Number(productForm.cost_price) || 0,
        sell_price: Number(productForm.sell_price) || 0,
        min_stock: Number(productForm.min_stock) || 0,
        is_active: true,
      })
      setProductForm({ name: '', barcode: '', cost_price: '', sell_price: '', min_stock: '0' })
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save product.'))
    } finally {
      setBusy(false)
    }
  }

  async function handlePurchase(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      const receipt = await createGoodsReceipt({
        supplier_id: purchaseForm.supplier_id ? Number(purchaseForm.supplier_id) : null,
        lines: [
          {
            product_id: Number(purchaseForm.product_id),
            quantity: Number(purchaseForm.quantity),
            unit_cost: Number(purchaseForm.unit_cost) || 0,
          },
        ],
      })
      await postGoodsReceipt(receipt.id)
      setPurchaseForm((p) => ({ ...p, quantity: '1', unit_cost: '' }))
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not record purchase.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleAdjust(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await postStockAdjustment({
        product_id: Number(adjustForm.product_id),
        quantity_delta: Number(adjustForm.quantity_delta),
        reason_id: adjustForm.reason_id ? Number(adjustForm.reason_id) : null,
        notes: adjustForm.notes || undefined,
      })
      setAdjustForm((p) => ({ ...p, quantity_delta: '', notes: '' }))
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not adjust stock.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleAddSupplier(e) {
    e.preventDefault()
    if (!newSupplierName.trim()) return
    setBusy(true)
    try {
      await createSupplier({ name: newSupplierName.trim() })
      setNewSupplierName('')
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not add supplier.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleUpdateSupplier(e) {
    e.preventDefault()
    if (!editSupplierId || !editSupplierName.trim()) return
    setBusy(true)
    try {
      await updateSupplier(Number(editSupplierId), { name: editSupplierName.trim() })
      setEditSupplierId('')
      setEditSupplierName('')
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update supplier.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleReturn(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await postPurchaseReturn({
        product_id: Number(returnForm.product_id),
        quantity: Number(returnForm.quantity),
        supplier_id: returnForm.supplier_id ? Number(returnForm.supplier_id) : null,
        notes: returnForm.notes || undefined,
      })
      setReturnForm({ product_id: '', quantity: '1', supplier_id: '', notes: '' })
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not record purchase return.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleTransfer(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await postStockTransfer({
        product_id: Number(transferForm.product_id),
        quantity: Number(transferForm.quantity),
        from_branch_id: Number(activeBranch?.id),
        to_branch_id: Number(transferForm.to_branch_id),
        notes: transferForm.notes || undefined,
      })
      setTransferForm({ product_id: '', quantity: '1', to_branch_id: '', notes: '' })
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not transfer stock.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleDamage(e) {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await postStockDamage({
        product_id: Number(damageForm.product_id),
        quantity: Number(damageForm.quantity),
        notes: damageForm.notes || undefined,
      })
      setDamageForm({ product_id: '', quantity: '1', notes: '' })
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not record damage.'))
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading products…
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-4 md:p-6">
      <header className="flex items-start gap-3">
        <Boxes className="mt-1 h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Products</h1>
          <p className="text-sm text-muted-foreground">
            Stock, suppliers, returns, transfers, damage, and stock takes.
          </p>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        {TABS.map((item) => (
          <Button
            key={item.id}
            type="button"
            size="sm"
            variant={tab === item.id ? 'default' : 'outline'}
            onClick={() => setTab(item.id)}
          >
            {item.label}
            {item.id === 'low' && lowStock.length > 0 ? ` (${lowStock.length})` : ''}
          </Button>
        ))}
      </div>

      {error ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {tab === 'list' ? (
        <section className="space-y-4">
          <form onSubmit={handleCreateProduct} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
            <h2 className="sm:col-span-2 text-lg font-medium">Add product</h2>
            <input className={inputClass} placeholder="Name" required value={productForm.name} onChange={(e) => setProductForm((p) => ({ ...p, name: e.target.value }))} />
            <input className={inputClass} placeholder="Barcode (optional)" value={productForm.barcode} onChange={(e) => setProductForm((p) => ({ ...p, barcode: e.target.value }))} />
            <input className={inputClass} type="number" step="0.01" placeholder="Cost" value={productForm.cost_price} onChange={(e) => setProductForm((p) => ({ ...p, cost_price: e.target.value }))} />
            <input className={inputClass} type="number" step="0.01" placeholder="Sell price" required value={productForm.sell_price} onChange={(e) => setProductForm((p) => ({ ...p, sell_price: e.target.value }))} />
            <input className={inputClass} type="number" step="0.01" placeholder="Min stock" value={productForm.min_stock} onChange={(e) => setProductForm((p) => ({ ...p, min_stock: e.target.value }))} />
            <Button type="submit" disabled={busy} className="sm:col-span-2 w-fit">
              Save product
            </Button>
          </form>
          <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
            {products.length === 0 ? (
              <li className="p-4 text-sm text-muted-foreground">No products yet.</li>
            ) : (
              products.map((product) => (
                <li key={product.id} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                  <div>
                    <p className="font-medium">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Sell AED {product.sell_price ?? product.price ?? '—'}
                      {product.barcode ? ` · ${product.barcode}` : ''}
                    </p>
                  </div>
                  <span className="font-medium">
                    On hand: {balanceByProduct.get(Number(product.id)) ?? product.quantity_on_hand ?? 0}
                  </span>
                </li>
              ))
            )}
          </ul>
        </section>
      ) : null}

      {tab === 'purchase' ? (
        <section className="space-y-4">
          <form onSubmit={handlePurchase} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
            <h2 className="sm:col-span-2 text-lg font-medium">Receive stock (purchase)</h2>
            <select className={inputClass} value={purchaseForm.supplier_id} onChange={(e) => setPurchaseForm((p) => ({ ...p, supplier_id: e.target.value }))}>
              <option value="">Supplier (optional)</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select className={inputClass} required value={purchaseForm.product_id} onChange={(e) => setPurchaseForm((p) => ({ ...p, product_id: e.target.value }))}>
              <option value="">Product</option>
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <input className={inputClass} type="number" min="0.01" step="0.01" required placeholder="Qty" value={purchaseForm.quantity} onChange={(e) => setPurchaseForm((p) => ({ ...p, quantity: e.target.value }))} />
            <input className={inputClass} type="number" min="0" step="0.01" placeholder="Unit cost" value={purchaseForm.unit_cost} onChange={(e) => setPurchaseForm((p) => ({ ...p, unit_cost: e.target.value }))} />
            <Button type="submit" disabled={busy} className="sm:col-span-2 w-fit">Receive &amp; post</Button>
          </form>
          <ul className="text-sm text-muted-foreground">
            {receipts.slice(0, 5).map((r) => (
              <li key={r.id}>GRN #{r.id} · {r.status}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {tab === 'suppliers' ? (
        <section className="space-y-4">
          <form onSubmit={handleAddSupplier} className="flex flex-wrap gap-2 rounded-2xl border border-border/70 bg-card p-4">
            <input className={cn(inputClass, 'max-w-xs')} placeholder="New supplier name" value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} />
            <Button type="submit" variant="outline" disabled={busy}>Add supplier</Button>
          </form>
          <form onSubmit={handleUpdateSupplier} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
            <h2 className="sm:col-span-2 text-lg font-medium">Rename supplier</h2>
            <select
              className={inputClass}
              value={editSupplierId}
              onChange={(e) => {
                const id = e.target.value
                setEditSupplierId(id)
                const match = suppliers.find((s) => String(s.id) === id)
                setEditSupplierName(match?.name ?? '')
              }}
            >
              <option value="">Supplier</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <input className={inputClass} placeholder="Name" value={editSupplierName} onChange={(e) => setEditSupplierName(e.target.value)} />
            <Button type="submit" disabled={busy} className="w-fit">Save</Button>
          </form>
          <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
            {suppliers.length === 0 ? (
              <li className="p-4 text-sm text-muted-foreground">No suppliers yet.</li>
            ) : (
              suppliers.map((s) => (
                <li key={s.id} className="px-4 py-3 text-sm font-medium">{s.name}</li>
              ))
            )}
          </ul>
        </section>
      ) : null}

      {tab === 'returns' ? (
        <form onSubmit={handleReturn} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
          <h2 className="sm:col-span-2 text-lg font-medium">Purchase return</h2>
          <select className={inputClass} required value={returnForm.product_id} onChange={(e) => setReturnForm((p) => ({ ...p, product_id: e.target.value }))}>
            <option value="">Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select className={inputClass} value={returnForm.supplier_id} onChange={(e) => setReturnForm((p) => ({ ...p, supplier_id: e.target.value }))}>
            <option value="">Supplier (optional)</option>
            {suppliers.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <input className={inputClass} type="number" min="0.01" step="0.01" required value={returnForm.quantity} onChange={(e) => setReturnForm((p) => ({ ...p, quantity: e.target.value }))} />
          <input className={inputClass} placeholder="Notes" value={returnForm.notes} onChange={(e) => setReturnForm((p) => ({ ...p, notes: e.target.value }))} />
          <Button type="submit" disabled={busy} className="w-fit sm:col-span-2">Record return</Button>
        </form>
      ) : null}

      {tab === 'transfer' ? (
        <form onSubmit={handleTransfer} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
          <h2 className="sm:col-span-2 text-lg font-medium">Branch transfer</h2>
          <select className={inputClass} required value={transferForm.product_id} onChange={(e) => setTransferForm((p) => ({ ...p, product_id: e.target.value }))}>
            <option value="">Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select className={inputClass} required value={transferForm.to_branch_id} onChange={(e) => setTransferForm((p) => ({ ...p, to_branch_id: e.target.value }))}>
            <option value="">To branch</option>
            {(branches ?? [])
              .filter((b) => Number(b.id) !== Number(activeBranch?.id))
              .map((b) => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
          </select>
          <input className={inputClass} type="number" min="0.01" step="0.01" required value={transferForm.quantity} onChange={(e) => setTransferForm((p) => ({ ...p, quantity: e.target.value }))} />
          <input className={inputClass} placeholder="Notes" value={transferForm.notes} onChange={(e) => setTransferForm((p) => ({ ...p, notes: e.target.value }))} />
          <Button type="submit" disabled={busy || !activeBranch?.id} className="w-fit sm:col-span-2">Transfer</Button>
        </form>
      ) : null}

      {tab === 'damage' ? (
        <form onSubmit={handleDamage} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
          <h2 className="sm:col-span-2 text-lg font-medium">Damage write-off</h2>
          <select className={inputClass} required value={damageForm.product_id} onChange={(e) => setDamageForm((p) => ({ ...p, product_id: e.target.value }))}>
            <option value="">Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input className={inputClass} type="number" min="0.01" step="0.01" required value={damageForm.quantity} onChange={(e) => setDamageForm((p) => ({ ...p, quantity: e.target.value }))} />
          <input className={cn(inputClass, 'sm:col-span-2')} placeholder="Notes" value={damageForm.notes} onChange={(e) => setDamageForm((p) => ({ ...p, notes: e.target.value }))} />
          <Button type="submit" disabled={busy} className="w-fit">Record damage</Button>
        </form>
      ) : null}

      {tab === 'stocktakes' ? (
        <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
          {stockTakes.length === 0 ? (
            <li className="p-4 text-sm text-muted-foreground">No stock takes yet.</li>
          ) : (
            stockTakes.map((take) => (
              <li key={take.id} className="px-4 py-3 text-sm">
                <p className="font-medium">Stock take #{take.id}</p>
                <p className="text-xs capitalize text-muted-foreground">{take.status ?? 'draft'}</p>
              </li>
            ))
          )}
        </ul>
      ) : null}

      {tab === 'adjust' ? (
        <form onSubmit={handleAdjust} className="grid gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2">
          <h2 className="sm:col-span-2 text-lg font-medium">Adjust stock</h2>
          <select className={inputClass} required value={adjustForm.product_id} onChange={(e) => setAdjustForm((p) => ({ ...p, product_id: e.target.value }))}>
            <option value="">Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <input className={inputClass} type="number" step="0.01" required placeholder="Change (+/-)" value={adjustForm.quantity_delta} onChange={(e) => setAdjustForm((p) => ({ ...p, quantity_delta: e.target.value }))} />
          <select className={inputClass} value={adjustForm.reason_id} onChange={(e) => setAdjustForm((p) => ({ ...p, reason_id: e.target.value }))}>
            <option value="">Reason (optional)</option>
            {reasons.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
          <input className={inputClass} placeholder="Notes" value={adjustForm.notes} onChange={(e) => setAdjustForm((p) => ({ ...p, notes: e.target.value }))} />
          <Button type="submit" disabled={busy} className="sm:col-span-2 w-fit">Save adjustment</Button>
        </form>
      ) : null}

      {tab === 'low' ? (
        <ul className="divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
          {lowStock.length === 0 ? (
            <li className="p-4 text-sm text-muted-foreground">Nothing below minimum stock.</li>
          ) : (
            lowStock.map((product) => (
              <li key={product.id} className="px-4 py-3 text-sm">
                <p className="font-medium text-amber-800">{product.name}</p>
                <p className="text-xs text-muted-foreground">
                  On hand {product.quantity_on_hand ?? 0} · Min {product.min_stock ?? 0}
                </p>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}
